/**
 * The 18+ check at signup.
 *
 * Why it exists: the Terms and the privacy policy both say GrowthSync is for over-18s, and for a
 * long time nothing enforced it. The `date_of_birth` and `age_verified` columns existed and were
 * read downstream (matching, badges, the admin view) but signup never asked, so the claim was
 * decoration. Under the Online Safety Act a service has to be able to say what it actually does to
 * keep children out, and "our terms say so" is not a measure.
 *
 * This is a self-declared age check. It is deliberately not called "age assurance": that term means
 * something specific and stronger under Ofcom's guidance, and claiming it would be the same kind of
 * overstatement this replaces. What it gives us is a recorded date of birth, a refusal for anyone
 * under 18, and a defensible answer to "how do you apply your own minimum age".
 */
export const MINIMUM_AGE_YEARS = 18;

/** Oldest date we will accept, to reject typos and obvious junk rather than store them. */
const MAX_AGE_YEARS = 120;

export type AgeCheck =
  | { ok: true; dateOfBirth: Date }
  | { ok: false; reason: "missing" | "malformed" | "future" | "implausible" | "under_age"; message: string };

/** Whole years between a date of birth and a reference date, counting an unreached birthday as not yet had. */
export function ageInYearsOn(dateOfBirth: Date, on: Date): number {
  let age = on.getUTCFullYear() - dateOfBirth.getUTCFullYear();
  const monthDelta = on.getUTCMonth() - dateOfBirth.getUTCMonth();
  if (monthDelta < 0 || (monthDelta === 0 && on.getUTCDate() < dateOfBirth.getUTCDate())) {
    age -= 1;
  }
  return age;
}

/**
 * Validate a `YYYY-MM-DD` date of birth from the signup form.
 * Messages are written for the person reading them, never the developer.
 */
export function checkSignupAge(value: unknown, now: Date = new Date()): AgeCheck {
  if (typeof value !== "string" || value.trim() === "") {
    return { ok: false, reason: "missing", message: "Please enter your date of birth." };
  }

  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (!match) {
    return { ok: false, reason: "malformed", message: "Please enter your date of birth as a day, month and year." };
  }

  const [, y, m, d] = match;
  const year = Number(y);
  const month = Number(m);
  const day = Number(d);
  const parsed = new Date(Date.UTC(year, month - 1, day));

  // Rejects 31 February and friends: the round trip only survives a real calendar date.
  const isRealDate =
    parsed.getUTCFullYear() === year &&
    parsed.getUTCMonth() === month - 1 &&
    parsed.getUTCDate() === day;
  if (!isRealDate) {
    return { ok: false, reason: "malformed", message: "That date does not look right. Please check and try again." };
  }

  if (parsed.getTime() > now.getTime()) {
    return { ok: false, reason: "future", message: "That date is in the future. Please check and try again." };
  }

  const age = ageInYearsOn(parsed, now);
  if (age > MAX_AGE_YEARS) {
    return { ok: false, reason: "implausible", message: "That date does not look right. Please check and try again." };
  }

  if (age < MINIMUM_AGE_YEARS) {
    return {
      ok: false,
      reason: "under_age",
      message: "You need to be 18 or over to use GrowthSync.",
    };
  }

  return { ok: true, dateOfBirth: parsed };
}
